import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAuditLogs, getStaff } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  ArrowCounterClockwise,
  ShoppingCart,
  HandCoins,
  Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AuditLogPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [selectedAction, setSelectedAction] = useState('all');

  const isAdmin = user?.role === 'admin' || user?.role === 'assistant';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const entityType = selectedEntity === 'all' ? undefined : selectedEntity;
      const actionType = selectedAction === 'all' ? undefined : selectedAction;
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      
      const [logsRes, staffRes] = await Promise.all([
        getAuditLogs(entityType, actionType, staffId, 200),
        getStaff()
      ]);
      setLogs(logsRes.data);
      setStaffList(staffRes.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [selectedEntity, selectedAction, selectedStaff]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'booking_confirmed':
        return <ShoppingCart size={18} className="text-blue-600" />;
      case 'booking_reversed':
        return <ArrowCounterClockwise size={18} className="text-purple-600" />;
      case 'fund_approved':
        return <CheckCircle size={18} className="text-green-600" />;
      case 'fund_rejected':
        return <XCircle size={18} className="text-red-600" />;
      case 'fund_reversed':
        return <ArrowCounterClockwise size={18} className="text-purple-600" />;
      default:
        return <Eye size={18} className="text-gray-500" />;
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'booking_confirmed': return 'Booking Confirmed';
      case 'booking_reversed': return 'Booking Reversed';
      case 'fund_approved': return 'Fund Approved';
      case 'fund_rejected': return 'Fund Rejected';
      case 'fund_reversed': return 'Fund Reversed';
      default: return action;
    }
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'booking_confirmed': return 'bg-blue-100 text-blue-700';
      case 'booking_reversed': return 'bg-purple-100 text-purple-700';
      case 'fund_approved': return 'bg-green-100 text-green-700';
      case 'fund_rejected': return 'bg-red-100 text-red-700';
      case 'fund_reversed': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (!isAdmin) {
    return <div className="p-4 text-red-600">Access denied. Admin only.</div>;
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Shield size={28} className="text-[#002FA7]" />
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {/* Entity Filter */}
          <SearchableSelect
            options={[
              { value: 'all', label: 'All Entities' },
              { value: 'booking', label: 'Bookings' },
              { value: 'fund_request', label: 'Fund Requests' }
            ]}
            value={selectedEntity}
            onValueChange={setSelectedEntity}
            placeholder="Entity Type"
            className="w-40"
            data-testid="entity-filter"
          />

          {/* Action Filter */}
          <SearchableSelect
            options={[
              { value: 'all', label: 'All Actions' },
              { value: 'booking_confirmed', label: 'Booking Confirmed' },
              { value: 'booking_reversed', label: 'Booking Reversed' },
              { value: 'fund_approved', label: 'Fund Approved' },
              { value: 'fund_rejected', label: 'Fund Rejected' },
              { value: 'fund_reversed', label: 'Fund Reversed' }
            ]}
            value={selectedAction}
            onValueChange={setSelectedAction}
            placeholder="Action Type"
            className="w-44"
            data-testid="action-filter"
          />

          {/* Staff Filter */}
          <SearchableSelect
            options={[
              { value: 'all', label: 'All Staff' },
              ...staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))
            ]}
            value={selectedStaff}
            onValueChange={setSelectedStaff}
            placeholder="Staff"
            searchPlaceholder="Search staff..."
            className="w-40"
            data-testid="staff-filter-audit"
          />

          <Button onClick={fetchData} className="btn-secondary" data-testid="refresh-btn">
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="stat-card">
          <div className="stat-label">Total Actions</div>
          <div className="stat-value">{logs.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Bookings</div>
          <div className="stat-value text-blue-600">
            {logs.filter(l => l.entity_type === 'booking').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Funds</div>
          <div className="stat-value text-green-600">
            {logs.filter(l => l.entity_type === 'fund_request').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Reversals</div>
          <div className="stat-value text-purple-600">
            {logs.filter(l => l.action_type.includes('reversed')).length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Rejections</div>
          <div className="stat-value text-red-600">
            {logs.filter(l => l.action_type.includes('rejected')).length}
          </div>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Done By</TableHead>
                <TableHead>Reason / Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow 
                    key={log.id} 
                    className={log.action_type.includes('reversed') ? 'bg-purple-50' : ''}
                    data-testid={`audit-row-${log.id}`}
                  >
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDateTime(log.created_at)}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded ${getActionBadgeClass(log.action_type)}`}>
                        {getActionIcon(log.action_type)}
                        {getActionLabel(log.action_type)}
                      </span>
                    </TableCell>
                    <TableCell className="text-gray-500 uppercase text-xs">
                      {log.entity_type?.replace('_', ' ')}
                    </TableCell>
                    <TableCell className="font-medium">{log.staff_name || '-'}</TableCell>
                    <TableCell className={`numeric ${log.action_type.includes('reversed') ? 'text-purple-600' : log.action_type.includes('approved') || log.action_type === 'booking_confirmed' ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(log.amount)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-[#002FA7]">{log.done_by_name}</span>
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      {log.reason && (
                        <span className="text-sm text-gray-600 italic">"{log.reason}"</span>
                      )}
                      {log.details && Object.keys(log.details).length > 0 && !log.reason && (
                        <span className="text-xs text-gray-500">
                          {Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')}
                        </span>
                      )}
                      {!log.reason && (!log.details || Object.keys(log.details).length === 0) && '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Info Card */}
      <div className="card-flat">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">About Audit Logs</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• All important actions are logged automatically</li>
          <li>• <span className="text-blue-600">Booking Confirmed</span> = New booking created</li>
          <li>• <span className="text-purple-600">Booking Reversed</span> = Admin cancelled confirmed booking (late refund)</li>
          <li>• <span className="text-green-600">Fund Approved</span> = Admin approved staff fund request</li>
          <li>• <span className="text-red-600">Fund Rejected</span> = Admin rejected staff fund request</li>
          <li>• <span className="text-purple-600">Fund Reversed</span> = Admin reversed an approved fund</li>
          <li>• Logs cannot be edited or deleted for transparency</li>
        </ul>
      </div>
    </div>
  );
}
